import './index.scss';
import React from 'react';
import moment from 'moment';
import IconButton from 'components/IconButton';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-semantic-toasts';
import {
  deleteResult,
  updateResult,
  getResult,
  withdrawResult,
  markResultQuoted,
  markResultDeclined,
} from 'services/results';
import { useAuth } from 'contexts/auth';
import { useData } from 'contexts/data';
import {
  Button,
  Dimmer,
  Header,
  Icon,
  Input,
  Label,
  Loader,
  Modal,
  Popup,
  Table,
} from 'semantic-ui/semantic-ui-imports';
import { Result } from '../../Types/Project';

interface Props {
  initialResults: Result[];
  projectId: string | number;
}

// Map status to Semantic UI color
const getStatusColor = (
  status?: string
): 'grey' | 'blue' | 'orange' | 'yellow' | 'green' | 'purple' | 'olive' => {
  switch (status) {
    case 'draft':
      return 'grey';
    case 'selected':
      return 'olive';
    case 'calculated':
      return 'blue';
    case 'offer_requested':
      return 'orange';
    case 'withdrawn':
      return 'yellow';
    case 'quoted':
      return 'green';
    case 'declined':
      return 'purple';
    default:
      return 'grey';
  }
};

const ResultList = ({ initialResults, projectId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isPartner } = useAuth();
  const {
    setActiveUserInputs,
    setActiveAggregate,
    setSelectedResultId,
    setAlternativeTo,
    loadSavedResultData,
  } = useData();
  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [isEditingName, setIsEditingName] = React.useState<boolean>(false);
  const [editingId, setEditingId] = React.useState<
    string | number | undefined
  >();
  const [openConfirm, setOpenConfirm] = React.useState<boolean>(false);
  const [openWithdrawConfirm, setOpenWithdrawConfirm] = React.useState<boolean>(
    false
  );
  const [openQuotedConfirm, setOpenQuotedConfirm] = React.useState<boolean>(
    false
  );
  const [openDeclinedConfirm, setOpenDeclinedConfirm] = React.useState<boolean>(
    false
  );
  const [results, setResults] = React.useState<Result[]>([...initialResults]);
  const [selectedResult, setSelectedResult] = React.useState<Result>();
  const [editedName, setEditedName] = React.useState<string>();

  const errorToast = () => {
    toast({
      type: 'error',
      title: t('common.somethingWentWrong'),
      time: 4000,
      size: 'small',
    });
  };

  const successToast = (message: string) => {
    toast({
      type: 'success',
      title: message,
      time: 4000,
      size: 'small',
    });
  };

  // Recalculate: fetch full result, load inputs only, clear aggregate, navigate to product selection
  const handleRecalculate = async (result: Result) => {
    setLoading(true);
    try {
      const fullResult = await getResult(result.id);
      if (!fullResult?.inputs) {
        errorToast();
        return;
      }
      setActiveUserInputs(fullResult.inputs);
      setActiveAggregate(null);
      setSelectedResultId(null);
      // For partners, track alternative_to
      if (isPartner) {
        setAlternativeTo(String(result.id));
      }
      navigate('/');
    } catch {
      errorToast();
    } finally {
      setLoading(false);
    }
  };

  // Duplicate: fetch full result, load inputs + aggregate, navigate to configure page
  const handleDuplicate = async (result: Result) => {
    const status = result.effective_status || result.status;
    if (status === 'draft') return; // Cannot duplicate drafts

    setLoading(true);
    try {
      const fullResult = await getResult(result.id);
      if (!fullResult?.data) {
        errorToast();
        return;
      }
      // Use loadSavedResultData to set both inputs and aggregate atomically
      // This preserves the addons from the saved result
      loadSavedResultData(fullResult.inputs, fullResult.data);
      setSelectedResultId(null);
      // For partners, track alternative_to
      if (isPartner) {
        setAlternativeTo(String(result.id));
      }
      navigate(`/configure/${fullResult.data.id}`);
    } catch {
      errorToast();
    } finally {
      setLoading(false);
    }
  };

  // Check if result can be duplicated (not a draft - selected is ok since user goes through Configure)
  const canDuplicate = (result: Result): boolean => {
    const status = result.effective_status || result.status;
    return status !== 'draft';
  };

  // Check if result is a draft
  const isDraft = (result: Result): boolean => {
    const status = result.effective_status || result.status;
    return status === 'draft';
  };

  // Check if result has aggregate selected but not fully configured
  const isSelected = (result: Result): boolean => {
    const status = result.effective_status || result.status;
    return status === 'selected';
  };

  // Check if result can request a quote (calculated or withdrawn status)
  const canRequestQuote = (result: Result): boolean => {
    const status = result.effective_status || result.status;
    return status === 'calculated' || status === 'withdrawn';
  };

  // Check if result can be withdrawn (offer_requested status)
  const canWithdraw = (result: Result): boolean => {
    const status = result.effective_status || result.status;
    return status === 'offer_requested';
  };

  // Navigate to request offer page
  const handleRequestQuote = (result: Result) => {
    navigate(`/projects/${projectId}/results/${result.id}/requestOffer`);
  };

  // Open withdraw confirmation modal
  const handleWithdraw = (result: Result) => {
    setSelectedResult(result);
    setOpenWithdrawConfirm(true);
  };

  // Confirm withdrawal - call API to update status
  const confirmWithdraw = async () => {
    if (!selectedResult) return;

    setLoading(true);
    try {
      // Only send status in body - id is in URL
      const updated = await withdrawResult(selectedResult.id);

      if (updated) {
        // Update local state
        const idx = results.findIndex((r) => r.id === selectedResult.id);
        if (idx > -1) {
          results[idx] = {
            ...results[idx],
            status: 'withdrawn',
            effective_status: 'withdrawn',
          };
          setResults([...results]);
        }
        successToast(t('resultList.withdrawSuccess'));
      }
    } catch {
      errorToast();
    } finally {
      setLoading(false);
      setOpenWithdrawConfirm(false);
      setSelectedResult(undefined);
    }
  };

  // Partner: Check if result can be marked as quoted (must be a quote request, not partner's own result)
  const canMarkQuoted = (result: Result): boolean => {
    // Only allow for user_request or alternative types (not partner's own user_result)
    if (
      result.result_type !== 'user_request' &&
      result.result_type !== 'alternative'
    ) {
      return false;
    }
    const status = result.effective_status || result.status;
    return status === 'offer_requested' || status === 'calculated';
  };

  // Partner: Check if result can be marked as declined (must be a quote request, not partner's own result)
  const canMarkDeclined = (result: Result): boolean => {
    // Only allow for user_request or alternative types (not partner's own user_result)
    if (
      result.result_type !== 'user_request' &&
      result.result_type !== 'alternative'
    ) {
      return false;
    }
    const status = result.effective_status || result.status;
    return status === 'offer_requested' || status === 'calculated';
  };

  // Partner: Check if mark quoted/declined buttons should be shown (alternative in selected or higher)
  const shouldShowMarkButtons = (result: Result): boolean => {
    if (result.result_type !== 'alternative') return canMarkQuoted(result);
    const status = result.effective_status || result.status;
    return (
      status === 'selected' ||
      status === 'calculated' ||
      status === 'offer_requested'
    );
  };

  // Partner: Open mark quoted confirmation modal
  const handleMarkQuoted = (result: Result) => {
    setSelectedResult(result);
    setOpenQuotedConfirm(true);
  };

  // Partner: Open mark declined confirmation modal
  const handleMarkDeclined = (result: Result) => {
    setSelectedResult(result);
    setOpenDeclinedConfirm(true);
  };

  // Partner: Confirm mark as quoted - call API to update status
  const confirmMarkQuoted = async () => {
    if (!selectedResult) return;

    setLoading(true);
    try {
      const updated = await markResultQuoted(selectedResult.id);

      if (updated) {
        // Update local state
        const idx = results.findIndex((r) => r.id === selectedResult.id);
        if (idx > -1) {
          results[idx] = {
            ...results[idx],
            status: 'quoted',
            effective_status: 'quoted',
          };
          setResults([...results]);
        }
        successToast(t('resultList.markQuotedSuccess'));
      }
    } catch {
      errorToast();
    } finally {
      setLoading(false);
      setOpenQuotedConfirm(false);
      setSelectedResult(undefined);
    }
  };

  // Partner: Confirm mark as declined - call API to update status
  const confirmMarkDeclined = async () => {
    if (!selectedResult) return;

    setLoading(true);
    try {
      const updated = await markResultDeclined(selectedResult.id);

      if (updated) {
        // Update local state
        const idx = results.findIndex((r) => r.id === selectedResult.id);
        if (idx > -1) {
          results[idx] = {
            ...results[idx],
            status: 'declined',
            effective_status: 'declined',
          };
          setResults([...results]);
        }
        successToast(t('resultList.markDeclinedSuccess'));
      }
    } catch {
      errorToast();
    } finally {
      setLoading(false);
      setOpenDeclinedConfirm(false);
      setSelectedResult(undefined);
    }
  };

  // Get result type label for partner view
  const getResultTypeLabel = (result: Result): string => {
    const resultType = result.result_type;
    if (resultType === 'alternative') {
      return t('resultList.type.alternative');
    }
    return t('resultList.type.userRequest');
  };

  // Check if result has been through the quoting flow
  const hasBeenThroughQuoting = (result: Result): boolean => {
    const status = result.effective_status || result.status;
    return ['offer_requested', 'withdrawn', 'quoted', 'declined'].includes(
      status || ''
    );
  };

  // Handle clicking on a result row
  const handleResultClick = async (result: Result) => {
    if (isDraft(result)) {
      // For drafts: load inputs, set selectedResultId, navigate to product selection
      setLoading(true);
      try {
        const fullResult = await getResult(result.id);
        if (!fullResult?.inputs) {
          errorToast();
          return;
        }
        // Use loadSavedResultData to avoid triggering unsaved changes detection
        loadSavedResultData(fullResult.inputs, null);
        setSelectedResultId(String(result.id));
        navigate('/');
      } catch {
        errorToast();
      } finally {
        setLoading(false);
      }
    } else if (isSelected(result)) {
      // For selected: load inputs + aggregate, navigate to configure page
      setLoading(true);
      try {
        const fullResult = await getResult(result.id);
        if (!fullResult?.data?.id) {
          errorToast();
          return;
        }
        loadSavedResultData(fullResult.inputs, fullResult.data);
        setSelectedResultId(String(result.id));
        navigate(`/configure/${fullResult.data.id}`);
      } catch {
        errorToast();
      } finally {
        setLoading(false);
      }
    } else {
      // For calculated and above: navigate to results page
      navigate(`/projects/${projectId}/results/${result.id}`);
    }
  };

  const goToResult = async (id: string | number, menuKey?: string) => {
    navigate(
      `/projects/${projectId}/results/${id}${menuKey ? `/${menuKey}` : ''}`
    );
  };

  const deleteResultSet = async (id: string | number) => {
    setLoading(true);
    try {
      await deleteResult(id);
      const idx = results.findIndex((r) => r.id === id);
      if (idx > -1) {
        results.splice(idx, 1);
        setResults([...results]);
      }
    } catch (err) {
      errorToast();
    }
    setOpenConfirm(false);
    setLoading(false);
  };

  const preventSelect = (ev: React.MouseEvent<Element, MouseEvent>) => {
    ev.preventDefault();
    ev.stopPropagation();
  };

  const updateName = async (r: Result, name: string) => {
    try {
      const newR = { ...r, name };
      await updateResult(newR);
      const idx = results.findIndex((x) => x.id === r.id);
      if (idx > -1) {
        results[idx] = newR;
        setResults([...results]);
      }
    } catch (err) {
      errorToast();
      console.error(err); // eslint-disable-line no-console
    }
    setEditedName('');
    setIsEditingName(false);
    setEditingId(undefined);
  };

  return (
    <>
      <Table celled selectable className="result-list">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>
              <h4>{t('common.name')}</h4>
            </Table.HeaderCell>
            {isPartner && (
              <Table.HeaderCell>
                <h4>{t('resultList.typeHeader')}</h4>
              </Table.HeaderCell>
            )}
            <Table.HeaderCell>
              <h4>{t('resultList.statusHeader')}</h4>
            </Table.HeaderCell>
            <Table.HeaderCell>
              <h4>{t('common.creationDate')}</h4>
            </Table.HeaderCell>
            <Table.HeaderCell>
              <h4>{t('resultList.offerRequestedHeader')}</h4>
            </Table.HeaderCell>
            <Table.HeaderCell>
              <h4>{t('common.pdf')}</h4>
            </Table.HeaderCell>
            <Table.HeaderCell>
              <h4>{t('resultList.actionsHeader')}</h4>
            </Table.HeaderCell>
            <Table.HeaderCell>
              <h4>{t('common.delete')}</h4>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {!!results?.length &&
            results.map((data: Result) => (
              <Table.Row key={data.id} onClick={() => handleResultClick(data)}>
                <Table.Cell width={isPartner ? 5 : 7}>
                  <div className="result-list__name">
                    {(!isEditingName || data.id !== editingId) && (
                      <>
                        <div>
                          {data.name}
                          {!hasBeenThroughQuoting(data) && (
                            <IconButton
                              icon="pencil"
                              onClick={(ev) => {
                                preventSelect(ev);
                                setEditingId(data.id);
                                setIsEditingName(true);
                              }}
                            />
                          )}
                        </div>
                        {data.alternative_to_name && (
                          <div className="result-list__alternative-to">
                            {t('resultList.alternativeTo', {
                              name: data.alternative_to_name,
                            })}
                          </div>
                        )}
                      </>
                    )}
                    {isEditingName && data.id === editingId && (
                      <>
                        <Input
                          name="name"
                          placeholder={t('common.name')}
                          labelPosition="right"
                          defaultValue={data.name || ''}
                          onChange={(e: { target: { value: string } }) => {
                            setEditedName(e.target.value);
                          }}
                          onClick={(
                            ev: React.MouseEvent<Element, MouseEvent>
                          ) => preventSelect(ev)}
                        />
                        <Button
                          onClick={(
                            ev: React.MouseEvent<Element, MouseEvent>
                          ) => {
                            preventSelect(ev);
                            if (editedName?.length) {
                              updateName(data, editedName);
                            } else {
                              setIsEditingName(false);
                            }
                          }}
                        >
                          {t('common.save')}
                        </Button>
                      </>
                    )}
                  </div>
                </Table.Cell>
                {isPartner && (
                  <Table.Cell width={2}>
                    <Label
                      color={
                        data.result_type === 'alternative' ? 'teal' : 'blue'
                      }
                      size="small"
                    >
                      {getResultTypeLabel(data)}
                    </Label>
                  </Table.Cell>
                )}
                <Table.Cell width={2}>
                  <Label
                    color={getStatusColor(data.effective_status || data.status)}
                    size="small"
                  >
                    {t(
                      `resultList.status.${
                        data.effective_status || data.status || 'calculated'
                      }`
                    )}
                  </Label>
                </Table.Cell>
                <Table.Cell width={2}>
                  {moment(data.created_at).calendar()}
                </Table.Cell>
                <Table.Cell width={3}>
                  {['offer_requested', 'quoted', 'declined'].includes(
                    data.status || ''
                  ) &&
                    data.offer_requested_date &&
                    moment(data.offer_requested_date).calendar()}
                </Table.Cell>
                <Table.Cell width={1}>
                  <IconButton
                    icon="file text"
                    onClick={(ev) => {
                      preventSelect(ev);
                      goToResult(data.id, 'downloadReport');
                    }}
                  />
                </Table.Cell>
                <Table.Cell width={2}>
                  <div className="result-list__actions">
                    <Popup
                      content={t('resultList.recalculateTooltip')}
                      trigger={
                        <span>
                          <IconButton
                            icon="refresh"
                            onClick={(ev) => {
                              preventSelect(ev);
                              handleRecalculate(data);
                            }}
                          />
                        </span>
                      }
                    />
                    <Popup
                      content={
                        canDuplicate(data)
                          ? t('resultList.duplicateTooltip')
                          : t('resultList.duplicateDisabled')
                      }
                      trigger={
                        <span>
                          <IconButton
                            icon="copy"
                            disabled={!canDuplicate(data)}
                            onClick={(ev) => {
                              preventSelect(ev);
                              handleDuplicate(data);
                            }}
                          />
                        </span>
                      }
                    />
                    {!isPartner && (
                      <>
                        {canRequestQuote(data) && (
                          <Popup
                            content={t('resultList.requestQuoteTooltip')}
                            trigger={
                              <span>
                                <IconButton
                                  icon="mail"
                                  onClick={(ev) => {
                                    preventSelect(ev);
                                    handleRequestQuote(data);
                                  }}
                                />
                              </span>
                            }
                          />
                        )}
                        {canWithdraw(data) && (
                          <Popup
                            content={t('resultList.withdrawTooltip')}
                            trigger={
                              <span>
                                <IconButton
                                  icon="undo"
                                  onClick={(ev) => {
                                    preventSelect(ev);
                                    handleWithdraw(data);
                                  }}
                                />
                              </span>
                            }
                          />
                        )}
                      </>
                    )}
                    {isPartner && (
                      <>
                        {shouldShowMarkButtons(data) && (
                          <Popup
                            content={
                              canMarkQuoted(data)
                                ? t('resultList.markQuotedTooltip')
                                : t('resultList.markQuotedDisabled')
                            }
                            trigger={
                              <span>
                                <IconButton
                                  icon="check"
                                  disabled={!canMarkQuoted(data)}
                                  onClick={(ev) => {
                                    preventSelect(ev);
                                    handleMarkQuoted(data);
                                  }}
                                />
                              </span>
                            }
                          />
                        )}
                        {shouldShowMarkButtons(data) && (
                          <Popup
                            content={
                              canMarkDeclined(data)
                                ? t('resultList.markDeclinedTooltip')
                                : t('resultList.markDeclinedDisabled')
                            }
                            trigger={
                              <span>
                                <IconButton
                                  icon="close"
                                  disabled={!canMarkDeclined(data)}
                                  onClick={(ev) => {
                                    preventSelect(ev);
                                    handleMarkDeclined(data);
                                  }}
                                />
                              </span>
                            }
                          />
                        )}
                      </>
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell width={1}>
                  {!hasBeenThroughQuoting(data) && (
                    <IconButton
                      icon="trash"
                      onClick={(ev) => {
                        preventSelect(ev);
                        setSelectedResult(data);
                        setOpenConfirm(true);
                      }}
                    />
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
        </Table.Body>
      </Table>
      <Modal
        onClose={() => setOpenConfirm(false)}
        open={openConfirm}
        size="small"
      >
        <Header icon>
          <Icon name="warning sign" color="red" />
          {t('resultList.deleteProject')}?
        </Header>
        <Modal.Content>
          <p>{`${t('resultList.modalText')} "${selectedResult?.name}"`}</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setOpenConfirm(false)}>
            <Icon name="cancel" /> {t('common.cancel')}
          </Button>
          <Button
            color="red"
            onClick={() => {
              if (selectedResult) {
                deleteResultSet(selectedResult.id);
              }
            }}
          >
            <Icon name="checkmark" /> {t('projectList.ok')}
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal
        onClose={() => setOpenWithdrawConfirm(false)}
        open={openWithdrawConfirm}
        size="small"
      >
        <Header icon>
          <Icon name="undo" color="orange" />
          {t('resultList.withdrawConfirm')}?
        </Header>
        <Modal.Content>
          <p>{`${t('resultList.withdrawModalText')} "${
            selectedResult?.name
          }"?`}</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setOpenWithdrawConfirm(false)}>
            <Icon name="cancel" /> {t('common.cancel')}
          </Button>
          <Button color="orange" onClick={confirmWithdraw}>
            <Icon name="checkmark" /> {t('common.yes')}
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal
        onClose={() => setOpenQuotedConfirm(false)}
        open={openQuotedConfirm}
        size="small"
      >
        <Header icon>
          <Icon name="check" color="green" />
          {t('resultList.markQuotedConfirm')}
        </Header>
        <Modal.Content>
          <p>{`${t('resultList.markQuotedModalText')} "${
            selectedResult?.name
          }"?`}</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setOpenQuotedConfirm(false)}>
            <Icon name="cancel" /> {t('common.cancel')}
          </Button>
          <Button color="green" onClick={confirmMarkQuoted}>
            <Icon name="checkmark" /> {t('common.yes')}
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal
        onClose={() => setOpenDeclinedConfirm(false)}
        open={openDeclinedConfirm}
        size="small"
      >
        <Header icon>
          <Icon name="close" color="purple" />
          {t('resultList.markDeclinedConfirm')}
        </Header>
        <Modal.Content>
          <p>{`${t('resultList.markDeclinedModalText')} "${
            selectedResult?.name
          }"?`}</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setOpenDeclinedConfirm(false)}>
            <Icon name="cancel" /> {t('common.cancel')}
          </Button>
          <Button color="purple" onClick={confirmMarkDeclined}>
            <Icon name="checkmark" /> {t('common.yes')}
          </Button>
        </Modal.Actions>
      </Modal>
      {isLoading && (
        <Dimmer inverted active page>
          <Loader size="large">Loading</Loader>
        </Dimmer>
      )}
    </>
  );
};

export default ResultList;
